/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useMemo, useState} from 'react'
import {useQuery} from '@tanstack/react-query'

import {getConcessionariesAll} from '../../../services/concessionariesService'
import {Loading} from '../../components/Loadings/loading'
import useSize from '../../../hooks/useSize'
import {useToast} from '../../components/Toats'
import {PaginationTablePlants} from '../../dashboard/TablePlants/components/Pagination'
import listLayoutStyles from '../../components/ListTableLayout/listTableLayout.module.css'
import {SelectGlobal} from '../../components/SelectGlobal'
import {SearchInputV3} from '../../components/SearchInputV3'
import {KTSVG} from '../../../../_metronic/helpers'
import {TableBody} from './TableConcessionariesBody'
import {ConcessionariesSummaryCards} from './ConcessionariesSummaryCards'
import {ModalEditUnitConsumerMonitoring} from './ModalEditUnitConsumerMonitoring'
import {useAuth} from '../../../modules/auth'

type Props = {
  className: string
}

type PaginationType = {
  page: number
  limit: number
  total: number
  qtdPages: number
}

const PAGE_SIZE = 15

const STATUS_MON_AUT_OPTIONS = [
  {name: 'MONITORADA'},
  {name: 'NÃO MONITORADA'},
  {name: 'EM MANUTENÇÃO'},
]

const STATUS_EXTRACT_OPTIONS = [{name: 'EXTRAI'}, {name: 'NAO EXTRAI'}, {name: 'SALVA'}]

const TYPE_MONITOR_OPTIONS = [{name: 'EMAIL'}, {name: 'CREDENCIAIS'}]

const FILTER_ICON = '/media/icons/duotune/general/gen031.svg'

const countByField = (items: any[], field: string, options: string[]) =>
  options.map((label) => ({
    label,
    count: items.filter((item) => item[field] === label).length,
  }))

const STATUS_MON_AUT_COLORS: Record<string, string> = {
  MONITORADA: 'success',
  'NÃO MONITORADA': 'danger',
  'EM MANUTENÇÃO': 'info',
}

const STATUS_EXTRACT_COLORS: Record<string, string> = {
  EXTRAI: 'success',
  'NAO EXTRAI': 'danger',
  SALVA: 'warning',
}

const TYPE_MONITOR_COLORS: Record<string, string> = {
  EMAIL: 'primary',
  CREDENCIAIS: 'info',
}

type FiltersDropdownProps = {
  expanded: boolean
  screenWidth: number
  filterStatusMonAut: string
  filterStatusExtract: string
  filterTypeMonitor: string
  setFilterStatusMonAut: (value: string) => void
  setFilterStatusExtract: (value: string) => void
  setFilterTypeMonitor: (value: string) => void
  setPagination: React.Dispatch<React.SetStateAction<PaginationType>>
  clearFilters: () => void
}

const FiltersDropdown: React.FC<FiltersDropdownProps> = ({
  expanded,
  screenWidth,
  filterStatusMonAut,
  filterStatusExtract,
  filterTypeMonitor,
  setFilterStatusMonAut,
  setFilterStatusExtract,
  setFilterTypeMonitor,
  setPagination,
  clearFilters,
}) => (
  <div className='dropdown'>
    <button
      type='button'
      className='btn btn-light btn-active-color-primary btn-sm dropdown-toggle h-100 d-inline-flex align-items-center flex-nowrap'
      data-bs-toggle='dropdown'
      aria-expanded='false'
      data-bs-auto-close='outside'
      style={{
        height: '3.5rem',
        whiteSpace: 'nowrap',
        gap: '0.5rem',
        ...(expanded
          ? {
              minWidth: screenWidth > 1200 ? '11rem' : '100%',
              width: screenWidth > 1200 ? 'auto' : '100%',
              paddingLeft: '1.25rem',
              paddingRight: '1.25rem',
              justifyContent: 'center',
            }
          : {
              justifyContent: 'center',
              paddingLeft: '0.85rem',
              paddingRight: '0.85rem',
            }),
      }}
    >
      <KTSVG path={FILTER_ICON} className='svg-icon-3 flex-shrink-0' />
      {expanded && <span className='fw-semibold flex-shrink-0'>Filtros</span>}
    </button>

    <div
      className={`dropdown-menu dropdown-menu-end p-4 ${listLayoutStyles.listFilterDropdownMenu}`}
    >
      <label
        className='form-label fw-bold'
        style={{marginTop: '0.5rem', marginBottom: '0.5rem'}}
      >
        Filtros
      </label>
      <div className='separator my-2'></div>

      <div style={{marginTop: '0.5rem', marginBottom: '1rem'}}>
        <SelectGlobal
          itens={STATUS_MON_AUT_OPTIONS}
          buttonAll={true}
          textSearch='Buscar status'
          textButtonInit='Monitoramento automático'
          labelKey='name'
          valueKey='name'
          typeIcon='svg'
          linkIcon={FILTER_ICON}
          sizeIconSvg='3'
          value={filterStatusMonAut}
          className='w-100'
          setPagination={setPagination}
          onChange={(value: string) => setFilterStatusMonAut(value || '')}
        />
      </div>

      <div style={{marginTop: '0.5rem', marginBottom: '1rem'}}>
        <SelectGlobal
          itens={STATUS_EXTRACT_OPTIONS}
          buttonAll={true}
          textSearch='Buscar extração'
          textButtonInit='Extração'
          labelKey='name'
          valueKey='name'
          typeIcon='svg'
          linkIcon={FILTER_ICON}
          sizeIconSvg='3'
          value={filterStatusExtract}
          className='w-100'
          setPagination={setPagination}
          onChange={(value: string) => setFilterStatusExtract(value || '')}
        />
      </div>

      <div style={{marginTop: '0.5rem', marginBottom: '1rem'}}>
        <SelectGlobal
          itens={TYPE_MONITOR_OPTIONS}
          buttonAll={true}
          textSearch='Buscar tipo'
          textButtonInit='Tipo de monitoramento'
          labelKey='name'
          valueKey='name'
          typeIcon='svg'
          linkIcon={FILTER_ICON}
          sizeIconSvg='3'
          value={filterTypeMonitor}
          className='w-100'
          setPagination={setPagination}
          onChange={(value: string) => setFilterTypeMonitor(value || '')}
        />
      </div>

      <div className='separator my-2'></div>

      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
        <a
          href='#'
          className='btn btn-color-gray-600 btn-active-color-primary'
          onClick={(e) => {
            e.preventDefault()
            clearFilters()
          }}
        >
          Limpar todos os filtros{' '}
          <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-5' />
        </a>
      </div>
    </div>
  </div>
)

const TableConcessionaries: React.FC<Props> = ({className}) => {
  const {width} = useSize()
  const {toggleToast} = useToast()
  const user = useAuth()

  const [filterName, setFilterName] = useState('')
  const [filterStatusMonAut, setFilterStatusMonAut] = useState('')
  const [filterStatusExtract, setFilterStatusExtract] = useState('')
  const [filterTypeMonitor, setFilterTypeMonitor] = useState('')

  const [showMonitoringModal, setShowMonitoringModal] = useState(false)
  const [selectedConcessionaire, setSelectedConcessionaire] = useState<any | null>(null)

  const [pagination, setPagination] = useState<PaginationType>({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    qtdPages: 1,
  })

  const {
    data: concessionaries = [],
    isLoading,
    isError,
  } = useQuery<any[]>({
    queryKey: ['concessionariesCatalog'],
    queryFn: async () => await getConcessionariesAll(),
    staleTime: 1000 * 60 * 5,
  })

  useEffect(() => {
    if (isError) toggleToast('Erro ao buscar as concessionárias!', 'error')
  }, [isError, toggleToast])

  const summaryStats = useMemo(() => {
    const monAutLabels = STATUS_MON_AUT_OPTIONS.map((o) => o.name)
    const extractLabels = STATUS_EXTRACT_OPTIONS.map((o) => o.name)
    const typeLabels = TYPE_MONITOR_OPTIONS.map((o) => o.name)

    return {
      total: concessionaries.length,
      statusMonAut: countByField(concessionaries, 'status_mon_aut', monAutLabels).map((item) => ({
        ...item,
        color: STATUS_MON_AUT_COLORS[item.label] || 'secondary',
      })),
      statusExtract: countByField(concessionaries, 'status_extract', extractLabels).map((item) => ({
        ...item,
        color: STATUS_EXTRACT_COLORS[item.label] || 'secondary',
      })),
      typeMonitor: countByField(concessionaries, 'type_monitor', typeLabels).map((item) => ({
        ...item,
        color: TYPE_MONITOR_COLORS[item.label] || 'secondary',
      })),
    }
  }, [concessionaries])

  const filteredConcessionaries = useMemo(() => {
    return concessionaries.filter((item) => {
      if (filterName && !item.name?.toLowerCase().includes(filterName.toLowerCase())) {
        return false
      }
      if (filterStatusMonAut && item.status_mon_aut !== filterStatusMonAut) return false
      if (filterStatusExtract && item.status_extract !== filterStatusExtract) return false
      if (filterTypeMonitor && item.type_monitor !== filterTypeMonitor) return false
      return true
    })
  }, [
    concessionaries,
    filterName,
    filterStatusMonAut,
    filterStatusExtract,
    filterTypeMonitor,
  ])

  const total = filteredConcessionaries.length
  const qtdPages = Math.max(1, Math.ceil(total / pagination.limit))
  const paginatedConcessionaries = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit
    return filteredConcessionaries.slice(start, start + pagination.limit)
  }, [filteredConcessionaries, pagination.page, pagination.limit])

  useEffect(() => {
    if (pagination.page > qtdPages) {
      setPagination((prev) => ({...prev, page: 1}))
    }
  }, [qtdPages, pagination.page])

  const start = total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1
  const end = Math.min(pagination.page * pagination.limit, total)
  const currentQtd = {start, end: end === 0 && total > 0 ? pagination.limit : end}

  const tablePagination = {
    ...pagination,
    total,
    qtdPages,
  }

  const clearFilters = () => {
    setFilterName('')
    setFilterStatusMonAut('')
    setFilterStatusExtract('')
    setFilterTypeMonitor('')
    setPagination((prev) => ({...prev, page: 1}))
  }

  const isAdmin = user.currentUser?.role.slug === 'ADMIN'

  if (isLoading) {
    return (
      <div
        style={{display: 'flex', justifyContent: 'center', height: '30rem', alignItems: 'center'}}
      >
        <Loading myClass='spinner-border' style={{width: '3rem', height: '3rem'}} />
      </div>
    )
  }

  return (
    <div className={`card ${className} ${listLayoutStyles.listCard}`}>
      <div className={`card-header border-0 pt-5 mb-4`}>
        <div className='w-100'>
          <ConcessionariesSummaryCards stats={summaryStats} />
        </div>

        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between',
            flexDirection: width > 1200 ? 'row' : 'column',
            flexWrap: 'nowrap',
            marginTop: '1rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '0.75rem',
              alignItems: 'center',
              flexWrap: 'nowrap',
              width: width > 1200 ? 'auto' : '100%',
              flex: '1',
            }}
          >
            <SearchInputV3
              className={`form-control form-control-sm bg-light ${listLayoutStyles.listSearch}`}
              placeholder='Buscar por nome'
              setMySearch={setFilterName}
              setPagination={setPagination}
              style={{
                //width: width > 1200 ? '40vw' : '100%',
                flex: '1',
                height: '3.5rem',
                flexShrink: 0,
              }}
            />

            {isAdmin && (
              <FiltersDropdown
                expanded={false}
                screenWidth={width}
                filterStatusMonAut={filterStatusMonAut}
                filterStatusExtract={filterStatusExtract}
                filterTypeMonitor={filterTypeMonitor}
                setFilterStatusMonAut={setFilterStatusMonAut}
                setFilterStatusExtract={setFilterStatusExtract}
                setFilterTypeMonitor={setFilterTypeMonitor}
                setPagination={setPagination}
                clearFilters={clearFilters}
              />
            )}
          </div>

          <div
            style={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              width: width > 1200 ? 'auto' : '100%',
              justifyContent: width > 1200 ? 'flex-end' : 'stretch',
            }}
          >
            {isAdmin ? (
              <button
                type='button'
                className='btn btn-sm btn-light-primary d-inline-flex align-items-center flex-nowrap'
                style={{
                  gap: '0.35rem',
                  height: '3.5rem',
                  whiteSpace: 'nowrap',
                  width: width > 1200 ? 'auto' : '100%',
                  justifyContent: 'center',
                }}
                onClick={() => toggleToast('Cadastro de concessionária em breve!', 'info')}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
                {width < 1200 ? 'Nova' : 'Nova Concessionária'}
              </button>
            ) : (
              <FiltersDropdown
                expanded={true}
                screenWidth={width}
                filterStatusMonAut={filterStatusMonAut}
                filterStatusExtract={filterStatusExtract}
                filterTypeMonitor={filterTypeMonitor}
                setFilterStatusMonAut={setFilterStatusMonAut}
                setFilterStatusExtract={setFilterStatusExtract}
                setFilterTypeMonitor={setFilterTypeMonitor}
                setPagination={setPagination}
                clearFilters={clearFilters}
              />
            )}
          </div>
        </div>
      </div>

      <div className={`card-body py-3 pb-2 ${listLayoutStyles.listTablePanel}`}>
        <div className={`table-responsive ${listLayoutStyles.listTableWrap}`}>
          <table className='table table-row-dashed table-hover table-row-gray-300 align-middle gs-0 gy-4'>
            <thead className='mt-0 pt-0'>
              <tr className='fw-bolder text-muted fs-7 text-uppercase gs-0'>
                <th className='min-w-250px'>Concessionária</th>
                {/* <th className='min-w-100px'>Estado</th> */}
                <th className='min-w-160px'>Monit. Automático</th>
                <th className='min-w-130px'>Extração</th>
                {/* <th className='min-w-130px'>Credenciais</th> */}
                <th className='min-w-130px'>Tipo Monitoramento</th>
                <th className='min-w-80px text-end'>Actions</th>
              </tr>
            </thead>
            <TableBody
              concessionaries={paginatedConcessionaries}
              onOpenMonitoring={(item) => {
                setSelectedConcessionaire(item)
                setShowMonitoringModal(true)
              }}
            />
          </table>
        </div>

        <PaginationTablePlants
          pagination={tablePagination}
          setPagination={setPagination}
          qtd={currentQtd}
        />
      </div>

      <ModalEditUnitConsumerMonitoring
        show={showMonitoringModal}
        concessionaire={selectedConcessionaire}
        handleClose={() => {
          setShowMonitoringModal(false)
          setSelectedConcessionaire(null)
        }}
      />
    </div>
  )
}

export {TableConcessionaries}
